import { useChatStoreActions } from "PFApp/stores/chat_store";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { useNotificationMarkAsRead } from "PFCore/hooks/queries/notifications/use_notification_mark_as_read";
import { UiRoute } from "PFCore/utilities/routes";
import logoURL from "PFImages/native_notification_icon.png";
import { FeatureFlag, Notification as NotificationType, NotificationCategoryType } from "PFTypes";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";

export const useNativeNotificationsHandler = () => {
  const navigate = useNavigate();
  const { mutate: markNotificationAsRead } = useNotificationMarkAsRead();
  const isNativeNotificationsEnabled = useIsFeatureEnabled(FeatureFlag.NativeNotifications);

  const { renderChat } = useChatStoreActions();

  const [notificationsAllowed, setNotificationsAllowed] = useState(false);

  const checkNotificationPromise = () => {
    try {
      Notification.requestPermission().then();
    } catch {
      return false;
    }
    return true;
  };

  const setupNotificationsInBrowser = () => {
    if (!("Notification" in window)) {
      setNotificationsAllowed(false);
    } else {
      if (checkNotificationPromise()) {
        Notification.requestPermission().then((permission) => {
          setNotificationsAllowed(permission === "granted");
        });
      } else {
        Notification.requestPermission((permission) => {
          setNotificationsAllowed(permission === "granted");
        });
      }
    }
  };

  useEffect(() => {
    if (isNativeNotificationsEnabled) {
      setupNotificationsInBrowser();
    } else {
      setNotificationsAllowed(false);
    }
  }, [isNativeNotificationsEnabled]);

  const handleCreateNativeNotification = useCallback(
    (notification: NotificationType) => {
      if (!notificationsAllowed) {
        return;
      }

      const { title, body, pictureUrl, categoryType, urlAddress, targetId, id } = notification;

      const nativeNotification = new Notification(title, {
        body: body,
        icon: pictureUrl || undefined,
        tag: categoryType,
        //@ts-ignore
        image: UiRoute(logoURL)
      });
      nativeNotification.onclick = () => {
        if (categoryType === NotificationCategoryType.Message) {
          renderChat(targetId);
        } else {
          navigate(urlAddress);
        }
        window.focus();
        markNotificationAsRead({ id });
        nativeNotification.close();
      };
    },
    [renderChat, markNotificationAsRead, navigate, notificationsAllowed]
  );

  return { handleCreateNativeNotification };
};
